<template>
    <sui-segment v-if="!isLoading" class="gateways-list">
        <div class="header-container">
            <div>
                <h1 is="sui-header">{{ $t('gateway.list.title', { name: activeCustomer.name }) }}</h1>
            </div>
            <div>
                <DynaliveImport
                    v-if="activeCustomer.gateways.length > 0"
                />
                <router-link
                    v-if="activeCustomer.gateways.length > 0"
                    class="ui violet button right floated"
                    :to="{ name: 'CustomerGatewayForm' }"><sui-icon name="plus"/>{{ $t('gateway.list.new') }}
                </router-link>
            </div>
        </div>

        <sui-table class="compact" celled striped selectable>
            <sui-table-header>
                <sui-table-row>
                    <sui-table-header-cell :width="1">{{ $t('common.id') }}</sui-table-header-cell>
                    <sui-table-header-cell :width="1">{{ $t('common.name') }}</sui-table-header-cell>
                    <sui-table-header-cell :width="2">{{ $t('gateway.list.lastProcess') }}</sui-table-header-cell>
                    <sui-table-header-cell :width="2">{{ $t('gateway.list.nextDate') }}</sui-table-header-cell>
                    <sui-table-header-cell :width="2">{{ $t('common.comment') }}</sui-table-header-cell>
                    <sui-table-header-cell :width="3">{{ $t('common.share') }}</sui-table-header-cell>
                    <sui-table-header-cell :width="4">{{ $t('common.actions') }}</sui-table-header-cell>
                </sui-table-row>
            </sui-table-header>

           <sui-table-body v-if="gateways.length === 0">
                <sui-table-row>
                    <sui-table-cell colspan="9" text-align="center">
                        <div>
                            <DynaliveImport />
                            <router-link
                                class="ui violet button"
                                :to="{ name: 'CustomerGatewayForm' }"><sui-icon name="plus"/>{{ $t('gateway.list.new') }}</router-link>
                        </div>
                    </sui-table-cell>
                </sui-table-row>
           </sui-table-body>

            <sui-table-body v-for="gateway in gateways" :key="gateway.id">
                <sui-table-row class="gateway">
                    <sui-table-cell colspan="6">{{ $t('gateway.config.software') }}: <b>{{ gateway.name }}</b></sui-table-cell>
                    <sui-table-cell text-align="center">
                        <router-link
                            :class="'ui tiny button'"
                            :to="{
                                name: 'CustomerGatewayOverrideForm',
                                params: {
                                    customer_id: activeCustomer.id,
                                    gateway_id: gateway.id
                                }
                            }"
                        >
                            {{ $t('common.override') }}
                        </router-link>
                    </sui-table-cell>
                </sui-table-row>

                <sui-table-row v-for="gatewayFile in gateway.gatewayFiles" :key="gatewayFile.id">
                    <sui-table-cell>{{ gatewayFile.id }}</sui-table-cell>
                    <sui-table-cell>{{ gatewayFile.name }}</sui-table-cell>
                    <sui-table-cell>
                        <GatewayProcessState :process="gatewayFile.lastProcess" />
                    </sui-table-cell>
                    <sui-table-cell>
                        <NextGatewayFileDownloadCronTaskInfos :cronExpression="gatewayFile.gateway.cronExpression" />
                    </sui-table-cell>
                    <sui-table-cell>
                        {{ gatewayFile.comment }}
                    </sui-table-cell>
                    <sui-table-cell>
                        <sui-list v-if="gatewayFile.sharedWithIds.length > 0">
                            <a is="sui-list-item" v-if="gatewayFile.sharedWithIds.includes(activeCustomer.id)">
                                <sui-icon color="violet" name="share"></sui-icon>
                                <sui-list-content>
                                    <sui-list-header>{{ $t('gateway.list.sharedBy') }}</sui-list-header>
                                    <sui-list-description>{{ gatewayFile.createdBy.name }}</sui-list-description>
                                </sui-list-content>
                            </a>

                            <a
                                is="sui-list-item"
                                v-if="gatewayFile.sharedWithIds.includes(activeCustomer.id) === false"
                            >
                                <sui-icon color="violet" name="reply"></sui-icon>
                                <sui-list-content>
                                    <sui-list-header>{{ $t('gateway.list.sharedWith') }}</sui-list-header>
                                    <sui-list-description v-for="partialCustomer in gatewayFile.sharedWith" :key="partialCustomer.id">
                                        {{ partialCustomer.name }}
                                    </sui-list-description>
                                </sui-list-content>
                            </a>
                        </sui-list>
                    </sui-table-cell>
                    <sui-table-cell text-align="center">
                        <router-link
                            :class="gatewayFile.hasConflicts ? 'ui tiny button right labeled icon' : 'ui tiny button'"
                            :to="{
                                name: 'CustomerGatewayForm',
                                params: {
                                    customer_id: gatewayFile.createdBy.id,
                                    gateway_file_id: gatewayFile.id
                                }
                            }"
                        >
                            <i v-if="gatewayFile.hasConflicts === true" class="icon orange exclamation triangle" />
                            {{ $t('common.config') }}
                        </router-link>

                        <sui-button
                            :disabled="gatewayFile.sharedWithIds.includes(activeCustomer.id)"
                            class="tiny"
                            @click.native="toggleModal(gatewayFile.id)"
                        >
                            {{ $t('common.share') }}
                        </sui-button>

                        <router-link
                            class="ui tiny button"
                            :to="{
                                name: 'GatewayProcessMonitoring',
                                params: {
                                    customer_id: activeCustomer.id,
                                    gateway_file_id: gatewayFile.id,
                                }
                            }">
                            {{ $t('common.monitoring') }}
                        </router-link>
                    </sui-table-cell>
                </sui-table-row>

            </sui-table-body>
        </sui-table>

        <sui-modal v-model="isModalOpen">
            <sui-modal-header>{{ $t('gateway.list.toShareWith') }}</sui-modal-header>
            <sui-modal-content>
                <sui-modal-description>
                    <ShareCustomerGatewayFileForm
                        :onSubmit="onShareCustomerFileSubmit"
                        :selectedFileId="currentSelectedFileId"
                        :customers="customersOptions"
                    />
                </sui-modal-description>
            </sui-modal-content>
            <sui-modal-actions>
                <sui-button class="violet" @click.native="onShareCustomerFileSubmit()">
                    {{ $t('common.toShare') }}
                </sui-button>
            </sui-modal-actions>
        </sui-modal>
    </sui-segment>
</template>

<script>
    import store from '@/store';
    import { mapGetters } from 'vuex';
    import ShareCustomerGatewayFileForm from '@/components/gateway-file/ShareCustomerGatewayFileForm';
    import GatewayProcessState from '@/components/gateway-file/GatewayProcessState.vue';
    import DynaliveImport from '@/components/dynalive/DynaliveImport.vue';
    import NextGatewayFileDownloadCronTaskInfos from '@/components/gateway/NextGatewayFileDownloadCronTaskInfos.vue';

    export default {
        name: 'CustomerGatewaysList',
        components: {
            ShareCustomerGatewayFileForm,
            GatewayProcessState,
            DynaliveImport,
            NextGatewayFileDownloadCronTaskInfos,
        },
        async beforeMount() {
            this.isLoading = true;

            await store.dispatch('getCustomerById', this.customerId);
            await store.dispatch('getCustomerGateways', this.customerId);
            await store.dispatch('getGateways');
            
            store.commit('setAppTitle', `${this.activeCustomer.name} - ${this.$t('common.gateways')}`);

            this.isLoading = false;
        },
        beforeDestroy() {
            store.commit('removeAppTitle');
        },
        data() {
            return {
                currentSelectedFileId: null,
                isModalOpen: false,
                isLoading: false,
                customerId: this.$route.params.customer_id,
            }
        },
        computed: {
            ...mapGetters([
                'customers',
                'gateways',
                'activeCustomer',
                'activeCustomerProcesses',
            ]),
            gateways: {
                get() {
                    if(this.activeCustomer && this.activeCustomer.gateways) {
                        const gateways = {};
                        this.activeCustomer.gateways.forEach(g => {
                            gateways[g.gateway.id] = gateways[g.gateway.id] || {
                                id: g.gateway.id,
                                name: g.gateway.name,
                                gatewayFiles: [],
                            };
                            gateways[g.gateway.id].gatewayFiles.push(g);
                        });
                        return Object.values(gateways);
                    }
                    return [];
                }
            },
            customersOptions: {
                get() {
                    if (this.customers) {
                        return this.customers.map(c => ({
                            text: c.name,
                            value: c.id,
                        }));
                    }

                    return [];
                }   
            }
        },
        methods: {
            async toggleModal(fileId) {
                await store.dispatch('getCustomers');
                this.isModalOpen = !this.isModalOpen;
                this.currentSelectedFileId = fileId || null;
            },
            async onShareCustomerFileSubmit() {
                await store.dispatch('shareGatewayFile');
                await store.dispatch('getCustomerGateways', this.customerId);
                this.toggleModal();
            },
            getGateway(id) {
                return this.gateways.find(g => g.id === id);
            },
            getLastestProcess(gatewayFileId) {
                return this
                    .activeCustomerProcesses
                    .sort((a, b) => a.id - b.id)
                    .find(acp => acp.gatewayFile.id === gatewayFileId)
            }
        }
    }
</script>

<style>
.gateway {
    background-color: #F2F2F2;
}
</style>
